import { useEffect, useRef } from "react";

/**
 * The parent <-> Advance Viewer (MedDream) postMessage channel.
 *
 * The Advance Viewer is a MedDream iframe, so the React app cannot see which study the
 * radiologist is looking at the way it can in the basic viewer (where clicking a thumbnail
 * calls setActiveStudyId directly). MedDream ships an integration channel for exactly this:
 * it posts to `window.opener || window.parent`, so an iframe parent receives its events.
 *
 * The channel is gated server-side by `security.postMessageWhitelist` in MedDream's
 * application.properties -- a comma-separated list of origins. While that list is empty
 * MedDream never emits COMMUNICATION_SERVICE_READY *and* silently drops every inbound
 * action, so nothing here works and nothing here errors either. The origin that must be
 * whitelisted is this app's origin, not the viewer's.
 *
 * Two events are needed, because MedDream has two ways of changing what you are looking at
 * and only announces one of them:
 *
 *   ACTIVE_CONTAINER_CHANGED fires when the radiologist clicks a different viewport. It
 *   carries only that viewport.
 *
 *   INSTANCE_CHANGED fires when the content of any viewport changes -- including loading a
 *   study into one from the thumbnail panel, which does NOT raise
 *   ACTIVE_CONTAINER_CHANGED even though MedDream does silently make that viewport the
 *   active one. It carries every non-empty viewport, and it also fires on every slice while
 *   scrolling a series, so it is only useful as a diff: which viewport is now showing a
 *   different study than it was a moment ago.
 */

const ACTION = {
  READY: "COMMUNICATION_SERVICE_READY",
  SUBSCRIBE: "SUBSCRIBE_EVENT",
  ACTIVE_CONTAINER_CHANGED: "ACTIVE_CONTAINER_CHANGED",
  INSTANCE_CHANGED: "INSTANCE_CHANGED",
};

/**
 * MedDream addresses studies by whatever identifier the token used, and reports them back
 * as `<identifier>__<storageName>` (the field is called studyInstanceUid, but it is not a
 * bare DICOM UID). Our V1 token payload addresses studies by the numeric Augmento study id,
 * so the first segment is usually the study id outright. The V2 (key-image) payload puts the
 * opened study on a V2 storage addressed by real StudyInstanceUID while its priors stay on
 * V1 by numeric id -- so both shapes can arrive in the same session and the UID form has to
 * be mapped back through the study data we already hold.
 *
 * Storage names contain underscores (AUGMENTOPACS_V1_DTK_CORE), so only the FIRST "__"
 * separates the identifier.
 */
export const resolveMedDreamStudyId = (compositeUid, studyInstanceUidMap) => {
  if (typeof compositeUid !== "string" || compositeUid === "") return null;

  const identifier = compositeUid.split("__")[0];
  if (identifier === "") return null;

  if (/^\d+$/.test(identifier)) {
    return parseInt(identifier, 10);
  }

  const mapped = studyInstanceUidMap ? studyInstanceUidMap[identifier] : undefined;
  return mapped == null ? null : mapped;
};

/**
 * Reduces an INSTANCE_CHANGED payload to { containerId: studyId }. Viewports without a
 * resolvable study are dropped rather than recorded as empty, so that a viewport briefly
 * reporting nothing mid-load does not read as "this viewport's study changed".
 */
const toContainerStudyMap = (viewports, studyInstanceUidMap) => {
  const map = {};
  if (!Array.isArray(viewports)) return map;
  viewports.forEach((viewport) => {
    if (!viewport || !viewport.containerId) return;
    const studyId = resolveMedDreamStudyId(
      viewport.studyInstanceUid,
      studyInstanceUidMap
    );
    if (studyId != null) map[viewport.containerId] = studyId;
  });
  return map;
};

/**
 * Subscribes to the viewer's viewport changes and reports the study the radiologist is
 * currently working in, as an Augmento study id.
 *
 * `studyInstanceUidMap` and `onActiveStudyChange` are read through refs so that a new map
 * (or a new callback identity) never tears down and re-subscribes the channel -- a
 * re-subscribe would register a second listener inside MedDream and double-fire events.
 */
export const useMedDreamActiveStudy = ({
  iframeRef,
  viewerUrl,
  studyInstanceUidMap,
  onActiveStudyChange,
}) => {
  const onActiveStudyChangeRef = useRef(onActiveStudyChange);
  onActiveStudyChangeRef.current = onActiveStudyChange;

  const studyInstanceUidMapRef = useRef(studyInstanceUidMap);
  studyInstanceUidMapRef.current = studyInstanceUidMap;

  useEffect(() => {
    if (!viewerUrl) return undefined;

    let viewerOrigin;
    try {
      viewerOrigin = new URL(viewerUrl, window.location.href).origin;
    } catch (e) {
      // A malformed viewer URL means there is no iframe to talk to either.
      return undefined;
    }

    // Last container MedDream told us is focused. Used to break ties when more than one
    // viewport's study changes at once.
    let activeContainerId = null;
    // { containerId: studyId } as last seen. Merged rather than replaced on each payload,
    // so a viewport that momentarily reports no study mid-load does not look like it was
    // removed and re-added -- that would downgrade a replacement to an appearance and the
    // change would be ignored.
    let containerStudies = {};
    // Only report genuine changes: MedDream re-fires on every viewport focus and every
    // slice, and most of those land on the study we already reported.
    let lastReported;

    const post = (actionType, actionData) => {
      const target = iframeRef.current && iframeRef.current.contentWindow;
      if (!target) return;
      target.postMessage({ actionType, actionData }, viewerOrigin);
    };

    const report = (studyId) => {
      if (studyId == null || lastReported === studyId) return;
      lastReported = studyId;
      if (onActiveStudyChangeRef.current) {
        onActiveStudyChangeRef.current(studyId);
      }
    };

    const onActiveContainerChanged = (actionData) => {
      if (!actionData || !actionData.containerId) return;
      activeContainerId = actionData.containerId;

      // An empty viewport fires the event with only a containerId and no study fields.
      // "Nothing is focused" must not be read as "the parent study is focused", or the
      // report panel would flip back to the parent (re-enabling Submit) on a stray click
      // into blank space. Hold the last known study instead.
      report(
        resolveMedDreamStudyId(
          actionData.studyInstanceUid,
          studyInstanceUidMapRef.current
        )
      );
    };

    const onInstanceChanged = (actionData) => {
      const next = toContainerStudyMap(actionData, studyInstanceUidMapRef.current);

      // A viewport that swaps one study for another is always the radiologist acting.
      //
      // A viewport that merely *appears* with a study in it is not: that is what the
      // initial hang looks like (every viewport appears at once), and following it would
      // put the panel on a hanging-protocol prior, action buttons disabled, before anyone
      // had touched the viewer. The exception is an appearance in the viewport MedDream
      // has already told us is focused -- clicking an empty viewport and then loading a
      // study into it is a normal way to bring up a prior, and it is only ever one
      // viewport. At the initial hang no viewport has been announced as focused yet, so
      // nothing qualifies.
      const changed = Object.keys(next).filter((containerId) => {
        const previous = containerStudies[containerId];
        if (previous === undefined) return containerId === activeContainerId;
        return next[containerId] !== previous;
      });
      containerStudies = { ...containerStudies, ...next };
      if (changed.length === 0) return;

      // Loading a study into a viewport makes that viewport active in MedDream, but it does
      // not announce that with ACTIVE_CONTAINER_CHANGED -- so the changed viewport is the
      // one the radiologist is now working in, and our idea of "active" has to follow it or
      // the next tie-break would use a stale container.
      const picked =
        activeContainerId && changed.indexOf(activeContainerId) !== -1
          ? activeContainerId
          : changed[changed.length - 1];

      activeContainerId = picked;
      report(next[picked]);
    };

    const onMessage = (event) => {
      if (event.origin !== viewerOrigin) return;
      const data = event.data;
      if (!data || !data.actionType) return;

      switch (data.actionType) {
        case ACTION.READY:
          post(ACTION.SUBSCRIBE, { eventType: ACTION.ACTIVE_CONTAINER_CHANGED });
          post(ACTION.SUBSCRIBE, { eventType: ACTION.INSTANCE_CHANGED });
          break;
        case ACTION.ACTIVE_CONTAINER_CHANGED:
          onActiveContainerChanged(data.actionData);
          break;
        case ACTION.INSTANCE_CHANGED:
          onInstanceChanged(data.actionData);
          break;
        default:
          break;
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [viewerUrl, iframeRef]);
};
